"use client"

import React, { Suspense } from "react"
import { useSearchParams } from "next/navigation"
import { SEARCH_PARAMS_FF_SEARCH_PARAMS, SEARCH_PARAMS_PAGE_VIEW_OPTIONS } from "@/constants/ui"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { StoryblokComponent } from "@storyblok/react/rsc"
import { ProductPriceContainerCtnrROV1, ProductPriceContainerRO } from "@ub/pricing-client"
import { Product } from "schema-dts"

import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import FallbackCard from "@/components/common/FallbackCard"
import ProductCardWrapper from "@/components/product/ProductCardWrapper"
import { getPaginationSearchParams } from "@/components/search-and-category/pagination-info"
import HtmlTextBox from "@/components/storyblok/common/HtmlTextBox"
import { UbProductCardStoryblok } from "@/components/storyblok/component-types"

const BannerTile = (props: { banner: string }) => (
  <div
    style={{ backgroundColor: "#1c3643" }}
    className="flex size-full justify-center rounded-lg border border-gray-300 bg-white prose-img:m-0 prose-img:rounded-lg"
  >
    <HtmlTextBox text={props.banner ?? ""} />
  </div>
)

export default function SearchResultContainerGridList(props: {
  banner: string | undefined
  bannerPosition: number | undefined
  products: ReducedProductData[]
  tracking: string
  templateBlok: any
  isB2B: boolean
  allowDebugMode: boolean | undefined
  hideAddToBasket: boolean | undefined
  inStoryBlok: boolean | undefined
  forceListView?: boolean
}) {
  const isBannerPosition = (index: number) =>
    props.banner !== undefined && props.bannerPosition !== undefined && index === props.bannerPosition - 1
  const isBannerAtLastPosition =
    props.banner !== undefined && props.bannerPosition !== undefined && props.bannerPosition > props.products.length
  const { articleListView } = useUbStorefrontState()
  const searchParams = useSearchParams()
  const ffSearchParamsEncrypted = searchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ?? undefined
  return (
    <>
      <div
        className={cn("grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4", {
          "@lg:!grid-cols-1 @lg:auto-rows-[minmax(1fr,auto)]":
            props.forceListView || articleListView == SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1],
        })}
      >
        {props.products.map((productData, index) => (
          <Suspense key={productData.id} fallback={<FallbackCard className={"bg-gray-100"} />}>
            <>
              {isBannerPosition(index) && <BannerTile banner={props.banner ?? ""} />}
              <ProductCardWrapper
                productData={productData}
                effectivePrices={productData.effectivePrices as ProductPriceContainerCtnrROV1}
                view={props.forceListView ? SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1] : articleListView}
                cardVariant={props.templateBlok.variant ?? "standard"}
                isB2B={props.isB2B}
                showEcoTax={props.templateBlok.showEcoTax ?? false}
                debugMode={props.templateBlok.debugMode && props.allowDebugMode}
                hideAddToBasket={props.hideAddToBasket ?? false}
                priceRequestLink={""}
                isoLocale={"de-DE"}
                trackingList={props.tracking ?? "direct"}
                position={index + 1}
                mainProductId={undefined}
                isRecommendation={props.templateBlok.isRecommendation}
                inStoryBlock={props.inStoryBlok}
                ffSearchParamsEncrypted={ffSearchParamsEncrypted}
              />
            </>
          </Suspense>
        ))}
        {isBannerAtLastPosition && <BannerTile banner={props.banner ?? ""} />}
      </div>
    </>
  )
}
